import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import { SITE_URL, definedTermLd, faqLd } from "@/lib/structuredData";
import { GLOSSARY, getTerm, relatedTerms, type GlossaryTerm } from "@/lib/glossary";
import { getTemplate } from "@/templates";

type TemplateCard = { slug: string; name: string; tagline: string; category: string };
type RelatedCard = { slug: string; term: string; definition: string };

type Props = {
  term: GlossaryTerm;
  templates: TemplateCard[];
  related: RelatedCard[];
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: GLOSSARY.map((t) => ({ params: { slug: t.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
  const term = getTerm(String(params?.slug ?? ""));
  if (!term) return { notFound: true };

  // Resolve rather than trust: a typo in a template slug fails the build here
  // instead of shipping a dead link on a page whose whole job is interlinking.
  const templates = term.templates.map((slug) => {
    const t = getTemplate(slug);
    if (!t) {
      throw new Error(
        `glossary: term "${term.slug}" lists unknown template "${slug}"`
      );
    }
    return { slug: t.slug, name: t.name, tagline: t.tagline, category: t.category };
  });

  const resolved = relatedTerms(term.slug);
  if (resolved.length !== term.related.length) {
    throw new Error(
      `glossary: term "${term.slug}" lists an unknown related term (${term.related.join(", ")})`
    );
  }

  return {
    props: {
      term,
      templates,
      related: resolved.map(({ slug, term: name, definition }) => ({
        slug,
        term: name,
        definition,
      })),
    },
  };
};

export default function GlossaryTermPage({ term, templates, related }: Props) {
  const pageUrl = `${SITE_URL}/learn/${term.slug}`;

  return (
    <SiteShell
      title={`What is a ${term.term.toLowerCase()}? — FrazzleCRM glossary`}
      description={term.definition}
      canonical={pageUrl}
      ogTitle={`${term.term} — plain-English definition`}
      ogType="article"
    >
      {/* The DefinedTerm description is the same string as the visible lede
          below — an assistant quoting the schema quotes the page. */}
      <JsonLd data={definedTermLd(term)} />
      <JsonLd data={faqLd(term.faq)} />

      <article>
        <section className="bg-gradient-to-b from-indigo-50/60 to-white">
          <div className="mx-auto max-w-3xl px-6 pb-8 pt-12 sm:pt-14">
            <Link
              href="/learn"
              className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600"
            >
              ← Glossary
            </Link>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-[30px] font-extrabold tracking-tight text-slate-900 sm:text-[38px]">
                {term.term}
              </h1>
              <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700">
                {term.category}
              </span>
            </div>
            {term.aka && (
              <p className="mt-2 text-[13px] text-slate-400">
                Also called: {term.aka.join(", ")}
              </p>
            )}
            {/* The citable definition. Keep in lockstep with the JSON-LD. */}
            <p className="mt-5 border-l-4 border-indigo-300 pl-4 text-[17px] font-medium leading-relaxed text-slate-800">
              {term.definition}
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-6 py-8">
          <div className="space-y-4">
            {term.body.map((p) => (
              <p key={p.slice(0, 40)} className="text-[15px] leading-[1.75] text-slate-700">
                {p}
              </p>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-6 pb-8">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-6">
            <h2 className="text-[18px] font-bold text-slate-900">
              How it works in FrazzleCRM
            </h2>
            <ul className="mt-4 space-y-2.5">
              {term.inFrazzle.map((point) => (
                <li
                  key={point.slice(0, 40)}
                  className="flex items-start gap-2.5 text-[14px] leading-relaxed text-slate-700"
                >
                  <span className="mt-0.5 shrink-0 text-emerald-500">✓</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {term.deepDive && (
          <section className="mx-auto max-w-3xl px-6 pb-8">
            <Link
              href={term.deepDive.href}
              className="group block rounded-2xl border border-indigo-200 bg-indigo-50/50 p-6 transition-shadow hover:shadow-md"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">
                Go deeper
              </span>
              <h2 className="mt-1 text-[17px] font-bold text-slate-900 group-hover:text-indigo-700">
                {term.deepDive.label} &rarr;
              </h2>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
                {term.deepDive.blurb}
              </p>
            </Link>
          </section>
        )}

        {term.gotcha && (
          <section className="mx-auto max-w-3xl px-6 pb-8">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <h2 className="text-[16px] font-bold text-amber-900">
                Where teams get this wrong
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-amber-900/90">
                {term.gotcha}
              </p>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-3xl px-6 pb-8">
          <h2 className="text-[18px] font-bold text-slate-900">Common questions</h2>
          <div className="mt-4 space-y-4">
            {term.faq.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
              >
                <h3 className="text-[14.5px] font-bold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {templates.length > 0 && (
          <section className="mx-auto max-w-3xl px-6 pb-8">
            <h2 className="text-[18px] font-bold text-slate-900">
              See it on a real map
            </h2>
            <p className="mt-1 text-[13.5px] text-slate-500">
              Free templates where this shows up as an actual node. Clone one and
              the diagram runs as the automation.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {templates.map((t) => (
                <Link
                  key={t.slug}
                  href={`/templates/${t.slug}`}
                  className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {t.category}
                  </span>
                  <h3 className="mt-1.5 text-[15px] font-bold text-slate-900 group-hover:text-indigo-700">
                    {t.name}
                  </h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                    {t.tagline}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mx-auto max-w-3xl px-6 pb-8">
            <h2 className="text-[18px] font-bold text-slate-900">Related terms</h2>
            <div className="mt-4 space-y-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  href={`/learn/${r.slug}`}
                  className="group block rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <h3 className="text-[14.5px] font-bold text-slate-900 group-hover:text-indigo-700">
                    {r.term}
                  </h3>
                  <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                    {r.definition}
                  </p>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-[13px] text-slate-500">
              Or browse{" "}
              <Link
                href="/learn"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                all {GLOSSARY.length} terms
              </Link>{" "}
              — grouped by what part of the funnel they belong to.
            </p>
          </section>
        )}

        <section className="mx-auto max-w-3xl px-6 pb-16">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-7 text-center">
            <h2 className="text-[19px] font-bold text-slate-900">
              Draw it once, and it runs
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-[14px] leading-relaxed text-slate-600">
              In FrazzleCRM the funnel map IS the automation — every node you
              draw is a real step, and every step you edit redraws the map.
              Free plan, no credit card.
            </p>
            <a
              href={`${APP_URL}/signup`}
              className="mt-5 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
            >
              Start free
            </a>
          </div>
        </section>
      </article>
    </SiteShell>
  );
}
